import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";

export const shellStyles = (theme: Theme, { width, top }: { width: number; top: number }) => {
	return ({
		// Top-anchored as in every Figma frame, so a modal doesn't jump as its content changes height.
		container: {
			alignItems: "flex-start",
		},
		paper: {
			width,
			// Never wider than the window: between the full-screen breakpoint and the modal's own width.
			maxWidth: "calc(100% - 64px)",
			marginTop: top,
			maxHeight: `calc(100% - ${top + 32}px)`,
			borderRadius: 8,
			padding: theme.spacing(3),
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(4),
			// The shared Dialog goes full screen below this width.
			[theme.breakpoints.down("sm")]: {
				width: "100%",
				maxWidth: "100%",
				marginTop: 0,
				maxHeight: "100%",
				borderRadius: 0,
			},
		},
	} satisfies Record<string, CSSObject>);
};

export const layoutStyles = (theme: Theme) => {
	return ({
		// Header, divider and content, 16px apart; scrolls when the modal is taller than the window.
		// It reaches into the paper's side padding and pads itself back, so the scrollbar sits at the
		// modal's edge rather than over the content, and a control that sits past the content edge (a
		// Switch's thumb) isn't clipped by the scroll box.
		body: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(2),
			overflowY: "auto",
			marginLeft: theme.spacing(-3),
			marginRight: theme.spacing(-3),
			paddingLeft: theme.spacing(3),
			paddingRight: theme.spacing(3),
		},
		// Title and divider as one block that stays at the top of the scrolling body. Its bottom padding
		// covers the content scrolling under it; the negative margin gives the 16px back so the layout
		// at rest is the same as the non-sticky one.
		stickyHeader: {
			position: "sticky",
			top: 0,
			zIndex: 1,
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(2),
			paddingBottom: theme.spacing(2),
			marginBottom: theme.spacing(-2),
			backgroundColor: theme.palette.background.paper,
		},
		header: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: 0,
		},
		titleRow: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
			color: theme.palette.custom.textPrimary,
		},
		close: {
			padding: 0,
			color: theme.palette.custom.textPrimary,
		},
		divider: {
			borderColor: theme.palette.custom.divider,
		},
		footer: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(2),
		},
		actions: {
			display: "flex",
			justifyContent: "flex-end",
			alignItems: "center",
			gap: theme.spacing(1),
		},
	} satisfies Record<string, CSSObject>);
};

export const linkStyles = () => {
	return ({
		// The theme strips every MuiLink's underline with !important; the Figma underlines links inside
		// modal copy, where the underline is the only thing marking them as links.
		link: {
			textDecoration: "underline !important",
		},
	} satisfies Record<string, CSSObject>);
};
